"use client"
import { useState, useEffect, useContext, useCallback } from "react"
import { Box, Card, CardContent, Typography, Chip, Button, IconButton, Tooltip, CircularProgress, Alert, Collapse, Divider, useTheme, alpha } from "@mui/material"
import { People as PeopleIcon, Refresh as RefreshIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, Computer as ComputerIcon, PhoneAndroid as PhoneIcon, TabletAndroid as TabletIcon } from "@mui/icons-material"
import { ErpContext } from "../../context/ErpContext.js"

const UsuariosEnLineaComponent = () => {
  const theme = useTheme()
  
  let getUsuariosEnLinea
  try {
    const context = useContext(ErpContext)
    getUsuariosEnLinea = context?.getUsuariosEnLinea
  } catch (error) {
    console.error("Error obteniendo contexto:", error)
  }

  const [usuarios, setUsuarios] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [ultimaActualizacion, setUltimaActualizacion] = useState(null)
  const [usuariosExpandidos, setUsuariosExpandidos] = useState(new Set())
  const [now, setNow] = useState(Date.now())

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now())
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  // Función para formatear tiempo relativo
  const tiempoRelativo = (fecha) => {
    if (!fecha) return "N/A"
    const ahora = new Date()
    const fechaObj = new Date(fecha)
    const diffMs = ahora - fechaObj
    const diffMins = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffMins < 1) return "Ahora mismo"
    if (diffMins < 60) return `Hace ${diffMins} minuto${diffMins > 1 ? "s" : ""}`
    if (diffHours < 24) return `Hace ${diffHours} hora${diffHours > 1 ? "s" : ""}`
    return `Hace ${diffDays} día${diffDays > 1 ? "s" : ""}`
  }

  // Función para obtener icono de dispositivo
  const getDispositivoIcono = (dispositivo) => {
    switch (dispositivo?.toLowerCase()) {
      case "mobile":
        return <PhoneIcon fontSize="small" />
      case "tablet":
        return <TabletIcon fontSize="small" />
      default:
        return <ComputerIcon fontSize="small" />
    }
  }

  const formatFechaHora = (fecha) => {
    if (!fecha) return "N/A"
    try {
      return new Date(fecha).toLocaleString()
    } catch {
      return "N/A"
    }
  }

  const formatTiempoRestante = (expTimestamp) => {
    if (!expTimestamp) return "Sin información"
    const diff = expTimestamp - now
    if (Number.isNaN(diff) || diff <= 0) {
      return "Expirada"
    }

    const totalSeconds = Math.floor(diff / 1000)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60

    const parts = []
    if (hours > 0) parts.push(`${hours}h`)
    parts.push(`${minutes.toString().padStart(2, "0")}m`)
    parts.push(`${seconds.toString().padStart(2, "0")}s`)
    return parts.join(" ")
  }

  // Cargar usuarios en línea
  const cargarUsuarios = useCallback(async () => {
    if (!getUsuariosEnLinea) {
      console.error("getUsuariosEnLinea no está disponible en el contexto")
      setError("Funcionalidad no disponible")
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      const data = await getUsuariosEnLinea({
        limiteActividad: 30
      })

      const usuariosNormalizados = (data.usuarios || []).map((usuario) => {
        const sesionesMap = new Map()

        ;(usuario.sesiones || []).forEach((sesion) => {
          const key = `${(sesion.dispositivo || "unknown").toLowerCase()}|${(sesion.ipAddress || "").trim()}|${(sesion.navegador || "").toLowerCase()}`
          const existente = sesionesMap.get(key)

          if (!existente) {
            sesionesMap.set(key, sesion)
            return
          }

          const existenteActividad = new Date(existente.ultimaActividad || existente.fechaCreacion || 0).getTime()
          const nuevaActividad = new Date(sesion.ultimaActividad || sesion.fechaCreacion || 0).getTime()

          if (nuevaActividad >= existenteActividad) {
            sesionesMap.set(key, sesion)
          }
        })

        const sesionesUnicas = Array.from(sesionesMap.values())

        const proximaExpiracion = sesionesUnicas.reduce((min, sesionActual) => {
          const exp = new Date(sesionActual.fechaExpiracion || 0).getTime()
          if (Number.isNaN(exp)) return min
          return exp < min ? exp : min
        }, Number.POSITIVE_INFINITY)

        const ultimaActividadMs = sesionesUnicas.reduce((max, sesionActual) => {
          const ts = new Date(sesionActual.ultimaActividad || sesionActual.fechaCreacion || 0).getTime()
          return ts > max ? ts : max
        }, 0)

        return {
          ...usuario,
          sesiones: sesionesUnicas,
          sesionesActivas: sesionesUnicas.length,
          proximaExpiracion: Number.isFinite(proximaExpiracion) ? proximaExpiracion : null,
          ultimaActividad:
            ultimaActividadMs > 0 ? new Date(ultimaActividadMs).toISOString() : usuario.ultimaActividad || null
        }
      })

      setUsuarios(usuariosNormalizados)
      setUltimaActualizacion(data.ultimaActualizacion)
    } catch (err) {
      console.error("Error cargando usuarios en línea:", err)
      setError("No se pudieron cargar los usuarios en línea")
    } finally {
      setLoading(false)
    }
  }, [getUsuariosEnLinea])

  // Cargar inicialmente
  useEffect(() => {
    cargarUsuarios()
  }, [cargarUsuarios])

  // Actualización automática cada 30 segundos
  useEffect(() => {
    if (!getUsuariosEnLinea) return

    const interval = setInterval(() => {
      cargarUsuarios()
    }, 30000)

    return () => clearInterval(interval)
  }, [cargarUsuarios, getUsuariosEnLinea])

  // Toggle expandir/colapsar usuario
  const toggleExpandirUsuario = (idUsuario) => {
    const nuevosExpandidos = new Set(usuariosExpandidos)
    if (nuevosExpandidos.has(idUsuario)) {
      nuevosExpandidos.delete(idUsuario)
    } else {
      nuevosExpandidos.add(idUsuario)
    }
    setUsuariosExpandidos(nuevosExpandidos)
  }

  // Filtrar usuarios según estado
  const totalUsuarios = usuarios.length
  const totalSesiones = usuarios.reduce((sum, u) => sum + u.sesionesActivas, 0)

  // Verificar si las funciones están disponibles
  if (!getUsuariosEnLinea) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Las funciones de gestión de sesiones no están disponibles. Por favor, verifique que el contexto esté correctamente configurado.
        </Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ overflow: "hidden" }}>
      <Card elevation={2} sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <PeopleIcon sx={{ fontSize: 28, color: "#5b5b5f" }} />
              <Typography variant="h5" component="h1" sx={{ fontWeight: 600, color: "#5b5b5f" }}>
                Usuarios en Línea
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={cargarUsuarios}
                disabled={loading}
                sx={{ textTransform: "none" }}
              >
                Actualizar
              </Button>
            </Box>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Estadísticas */}
          <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
            <Chip
              icon={<PeopleIcon />}
              label={`${totalUsuarios} usuario${totalUsuarios !== 1 ? "s" : ""} conectado${totalUsuarios !== 1 ? "s" : ""}`}
              color="primary"
              variant="outlined"
            />
            <Chip
              icon={<ComputerIcon />}
              label={`${totalSesiones} sesión${totalSesiones !== 1 ? "es" : ""} activa${totalSesiones !== 1 ? "s" : ""}`}
              color="secondary"
              variant="outlined"
            />
            {ultimaActualizacion && (
              <Chip
                label={`Última actualización: ${tiempoRelativo(ultimaActualizacion)}`}
                variant="outlined"
                size="small"
              />
            )}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading && usuarios.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : usuarios.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              No hay usuarios en línea
            </Alert>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {usuarios.map((usuario) => {
                const tiempoRestanteUsuario = formatTiempoRestante(usuario.proximaExpiracion)
                return (
                  <Card key={usuario.idUsuario} elevation={1} sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ p: 2 }}>
                    {/* Header del usuario */}
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {usuario.nombre} {usuario.apellido}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {usuario.usuario} • {usuario.rol}
                          </Typography>
                        </Box>
                        <Chip
                          label={`${usuario.sesionesActivas} sesión${usuario.sesionesActivas !== 1 ? "es" : ""}`}
                          variant="outlined"
                          size="small"
                        />
                        <Chip
                          label={`Tiempo restante: ${tiempoRestanteUsuario}`}
                          variant="outlined"
                          color="info"
                          size="small"
                        />
                        <Typography variant="caption" color="text.secondary">
                          Última actividad: {tiempoRelativo(usuario.ultimaActividad)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Tooltip title="Ver sesiones">
                          <IconButton
                            size="small"
                            onClick={() => toggleExpandirUsuario(usuario.idUsuario)}
                            sx={{ color: "#5b5b5f" }}
                          >
                            {usuariosExpandidos.has(usuario.idUsuario) ? (
                              <ExpandLessIcon />
                            ) : (
                              <ExpandMoreIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    {/* Sesiones expandidas */}
                    <Collapse in={usuariosExpandidos.has(usuario.idUsuario)}>
                      <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                          Sesiones Activas ({usuario.sesiones?.length || 0})
                        </Typography>
                        {usuario.sesiones && usuario.sesiones.length > 0 ? (
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            {usuario.sesiones.map((sesion) => (
                              <Box
                                key={sesion.id}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  p: 1.5,
                                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                                  borderRadius: 1,
                                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                                }}
                              >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
                                  {getDispositivoIcono(sesion.dispositivo)}
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {sesion.dispositivo} • {sesion.navegador} • {sesion.sistemaOperativo}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      IP: {sesion.ipAddress} • Inicio: {formatFechaHora(sesion.fechaCreacion)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Última actividad: {tiempoRelativo(sesion.ultimaActividad)} • Tiempo restante: {formatTiempoRestante(new Date(sesion.fechaExpiracion || 0).getTime())}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No hay sesiones activas
                          </Typography>
                        )}
                      </Box>
                    </Collapse>
                  </CardContent>
                  </Card>
                )
              })}
            </Box>
          )}
        </CardContent>
      </Card>

    </Box>
  )
}

export default UsuariosEnLineaComponent

